/**
 * NumberNamingQuestionScreen - PreK Number Naming Assessment
 *
 * Refactored to use shared hooks and components while preserving
 * all original business logic exactly.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PreKSettings, DiagnosticAssessmentResult } from './types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../hooks/usePreKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';

// Shared components and constants
import { AnswerChoicesGrid, AssessmentHeader } from './shared';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import ListenButton from '../common/ListenButton';
import {
  PREK_TRACKS,
  TIMER_CONSTANTS,
  FLUENCY_TARGETS_SEC,
  WARMUP_CONFIG,
  TOTAL_ASSESSMENT_QUESTIONS,
  generateAssessmentSequence,
  generateAnswerChoices,
  generateRandomValue,
  valueToFactId,
} from './preKConstants';

interface NumberNamingQuestionScreenProps {
  settings: PreKSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

const TRACK_ID = PREK_TRACKS.NAMING;

const NumberNamingQuestionScreen: React.FC<NumberNamingQuestionScreenProps> = ({
  settings,
  onComplete,
}) => {
  // ============================================================================
  // State Management
  // ============================================================================

  // Question tracking
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  // Warmup state
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);

  // Current digit state (separate for warmup and test to preserve exact behavior)
  const [currentWarmupDigit, setCurrentWarmupDigit] = useState(() =>
    generateRandomValue(TRACK_ID)
  );
  const [currentTestDigit, setCurrentTestDigit] = useState(() =>
    generateRandomValue(TRACK_ID)
  );

  // Answer state
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  // Timer state
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState<number>(
    TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]
  );
  const [questionTimerActive, setQuestionTimerActive] = useState(false);
  const [timerActive, setTimerActive] = useState(false);

  // TTS state
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);

  // Answer choices
  const targetDigit = isWarmup ? currentWarmupDigit : currentTestDigit;
  const [answerChoices, setAnswerChoices] = useState(() =>
    generateAnswerChoices(TRACK_ID, targetDigit)
  );

  // ============================================================================
  // Refs
  // ============================================================================

  const { user, updateUserField } = useAuth();
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const questionSequenceRef = useRef<number[]>([]);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = usePreKTTS();

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const determineFactStatus = useCallback(
    (isCorrect: boolean, timeSpentMs: number): FactStatus => {
      if (!isCorrect) {
        return 'learning';
      }
      const timeSpentSeconds = timeSpentMs / 1000;
      if (timeSpentSeconds > FLUENCY_TARGETS_SEC[TRACK_ID]) {
        return 'accuracyPractice';
      } else {
        return 'mastered';
      }
    },
    []
  );

  const handleTestComplete = useCallback(
    async (results: DiagnosticAssessmentResult) => {
      if (user?.userId) {
        try {
          await recordPageTransition({
            userId: user.userId,
            trackId: TRACK_ID,
            page: 'other',
          });
        } catch (error) {
          console.error('Error recording final page transition:', error);
        }

        // Save PreK assessment data to database and sync focusTrack
        try {
          const result = await completePreKAssessment(user.userId, TRACK_ID, {
            totalQuestions: results.totalQuestions || 0,
            correctAnswers: results.correctAnswers || 0,
            activeTimeElapsed: results.activeTimeElapsed || 0,
          });
          if (result.focusTrack) {
            updateUserField({ focusTrack: result.focusTrack });
          }
        } catch (error) {
          console.error('Failed to save PreK assessment data:', error);
        }
      }
      onComplete(results);
    },
    [user?.userId, onComplete, updateUserField]
  );

  const resetQuestionState = useCallback(() => {
    setTimerActive(false);
    setQuestionTimerActive(false);
    setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);
    setWrongAnswer(null);
    setSelectedAnswer(null);
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Set track ID and report page transition on mount
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', TRACK_ID);

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: TRACK_ID,
        page: 'assessment',
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      questionAccumulatedTimeRef.current = 0;
      questionActiveStartRef.current = Date.now();
    }
  }, [isWarmup, currentTestDigit]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Question-count completion (replaces wall clock timer)
  useEffect(() => {
    if (!isWarmup && totalQuestions >= TOTAL_ASSESSMENT_QUESTIONS && user?.userId && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      handleTestComplete({
        userId: user.userId,
        testResults: [],
        recommendedStartSkill: 1,
        placementReason: `Completed ${TOTAL_ASSESSMENT_QUESTIONS} questions`,
        completedAt: new Date(),
        totalQuestions,
        correctAnswers,
        activeTimeElapsed,
      });
    }
  }, [totalQuestions, isWarmup, user?.userId, handleTestComplete, correctAnswers, activeTimeElapsed]);

  // Active time tracking
  useEffect(() => {
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed((prev) => prev + TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Play TTS when question changes
  useEffect(() => {
    if (ttsHasPlayed) {
      return;
    }

    if (settings.speechEnabled) {
      const currentDigit = isWarmup ? currentWarmupDigit : currentTestDigit;
      const audioFile =
        QUESTION_SCREEN_AUDIO.tapNumber[
          currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber
        ];

      playTTSWithCallback(audioFile, () => {
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }).catch(() => {
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      });
    } else {
      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
    }
  }, [
    isWarmup,
    currentWarmupDigit,
    currentTestDigit,
    warmupCount,
    settings.speechEnabled,
    playTTSWithCallback,
  ]);

  // Update answer choices when target digit changes
  useEffect(() => {
    setAnswerChoices(generateAnswerChoices(TRACK_ID, targetDigit));
    setHasAnswered(false);
  }, [targetDigit]);

  // Reset hasAnswered when waiting for correct answer
  useEffect(() => {
    if (waitingForCorrectAnswer) {
      setHasAnswered(false);
    }
  }, [waitingForCorrectAnswer]);

  // Per-question countdown timer
  useEffect(() => {
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (
        questionTimeRemaining <= 0 &&
        questionTimerActive &&
        !isWarmup &&
        !hasAnswered
      ) {
        if (waitingForCorrectAnswer) {
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          setCurrentTestDigit(questionSequenceRef.current[nextIndex]);
          setTimerActive(false);
          setQuestionTimerActive(false);
          setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);
          setWrongAnswer(null);
          setTtsHasPlayed(false);
          setHasAnswered(false);
        } else {
          setTotalQuestions((prev) => prev + 1);

          // Report timeout as incorrect
          if (user?.userId) {
            try {
              const factId = valueToFactId(TRACK_ID, currentTestDigit);
              const factStatus = determineFactStatus(false, TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);

              progressQueue.enqueueProgressUpdate(user.userId, {
                facts: {
                  [factId]: {
                    attempts: 1,
                    correct: 0,
                    timeSpent:
                      questionAccumulatedTimeRef.current +
                      (questionActiveStartRef.current
                        ? Date.now() - questionActiveStartRef.current
                        : 0),
                    status: factStatus,
                  },
                },
              });
            } catch (error) {
              console.error(
                'Error reporting Pre-K number naming timeout progress:',
                error
              );
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          setCurrentTestDigit(questionSequenceRef.current[nextIndex]);
          setTimerActive(false);
          setQuestionTimerActive(false);
          setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);
          setWrongAnswer(null);
          setTtsHasPlayed(false);
          setHasAnswered(false);
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining((prev) => prev - TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    questionTimeRemaining,
    questionTimerActive,
    isWarmup,
    waitingForCorrectAnswer,
    currentQuestionIndex,
    currentTestDigit,
    totalQuestions,
    hasAnswered,
    user?.userId,
    determineFactStatus,
  ]);

  // ============================================================================
  // Warmup Progression
  // ============================================================================

  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= WARMUP_CONFIG.MAX_WARMUP_QUESTIONS) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    const newDigit = generateRandomValue(TRACK_ID, currentWarmupDigit);
    setCurrentWarmupDigit(newDigit);
    setWarmupCount(newWarmupCount);
    resetQuestionState();
  }, [
    waitingForCorrectAnswer,
    warmupCount,
    currentWarmupDigit,
    resetQuestionState,
  ]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    const sequence = generateAssessmentSequence(TRACK_ID);
    questionSequenceRef.current = sequence;
    setCurrentTestDigit(sequence[0]);
    resetQuestionState();
    setTtsHasPlayed(false);
  }, [resetQuestionState]);

  // ============================================================================
  // Test Progression
  // ============================================================================

  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
      setCurrentQuestionIndex(nextIndex);
      setCurrentTestDigit(questionSequenceRef.current[nextIndex]);
      resetQuestionState();
      return;
    }

    setCorrectAnswers((prev) => prev + 1);
    setTotalQuestions((prev) => prev + 1);
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
    setCurrentQuestionIndex(nextIndex);
    setCurrentTestDigit(questionSequenceRef.current[nextIndex]);
    resetQuestionState();
  }, [
    waitingForCorrectAnswer,
    currentQuestionIndex,
    resetQuestionState,
  ]);

  // ============================================================================
  // Answer Handler
  // ============================================================================

  const handleAnswerClick = useCallback(
    (selectedDigit: number) => {
      if (waitingForCorrectAnswer && selectedDigit !== targetDigit) return;
      if (!waitingForCorrectAnswer && hasAnswered) return;

      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
      setHasAnswered(true);
      setSelectedAnswer(selectedDigit);

      const currentDigit = isWarmup ? currentWarmupDigit : currentTestDigit;
      const isCorrect = selectedDigit === currentDigit;

      // Calculate response time
      let timerActiveResponseTime = 0;
      if (!isWarmup) {
        timerActiveResponseTime =
          questionAccumulatedTimeRef.current +
          (questionActiveStartRef.current
            ? Date.now() - questionActiveStartRef.current
            : 0);
      }

      // Report progress for test questions
      if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
        try {
          const factId = valueToFactId(TRACK_ID, currentDigit);
          const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting Pre-K progress:', error);
        }
      }

      // Handle answer logic
      if (isWarmup) {
        if (isCorrect) {
          playChime().then(() => {
            setTimeout(() => {
              setTtsHasPlayed(false);
              advanceWarmupQuestion();
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          });
        } else {
          setWrongAnswer(selectedDigit);
          setTimeout(() => setWrongAnswer(null), TIMER_CONSTANTS.WRONG_ANSWER_FLASH_MS);

          if (settings.speechEnabled) {
            const audioFile =
              QUESTION_SCREEN_AUDIO.tapNumber[
                currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber
              ];
            setTimeout(() => {
              playTTSWithCallback(audioFile).catch(() => {});
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          }

          setWaitingForCorrectAnswer(true);
        }
      } else {
        // Quiz mode: neutral feedback with chime for all answers, same timing
        if (!waitingForCorrectAnswer && !isCorrect) {
          setTotalQuestions((prev) => prev + 1);
        }

        playChime().then(() => {
          setTimeout(() => {
            setTtsHasPlayed(false);
            if (isCorrect) {
              advanceTestQuestion();
            } else {
              const nextIndex = currentQuestionIndex + 1;
              if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
              setCurrentQuestionIndex(nextIndex);
              setCurrentTestDigit(questionSequenceRef.current[nextIndex]);
              resetQuestionState();
            }
          }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
        });
      }
    },
    [
      isTTSPlaying,
      isWarmup,
      currentWarmupDigit,
      currentTestDigit,
      user,
      waitingForCorrectAnswer,
      settings.speechEnabled,
      determineFactStatus,
      playChime,
      playTTSWithCallback,
      advanceWarmupQuestion,
      advanceTestQuestion,
      hasAnswered,
      targetDigit,
      currentQuestionIndex,
      resetQuestionState,
    ]
  );

  // ============================================================================
  // Render
  // ============================================================================

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={false}
        wallClockTimeRemaining={0}
        wallClockStarted={false}
        isTTSPlaying={isTTSPlaying}
      />

      {/* Listen Button */}
      {settings.speechEnabled && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => {
              const audioFile = QUESTION_SCREEN_AUDIO.tapNumber[
                targetDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber
              ];
              playTTSWithCallback(audioFile);
            }}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area - Centered Answer Choices */}
      <div className="flex-1 flex items-center justify-center px-6">
        <AnswerChoicesGrid
          choices={answerChoices}
          correctAnswer={targetDigit}
          wrongAnswer={wrongAnswer}
          hasAnswered={hasAnswered}
          waitingForCorrectAnswer={waitingForCorrectAnswer}
          disabled={false}
          onChoiceClick={handleAnswerClick}
          selectedAnswer={selectedAnswer}
          neutralFeedbackMode={!isWarmup}
        />
      </div>
    </div>
  );
};

export default NumberNamingQuestionScreen;
